import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const COPY = {
  it: {
    privacy_a: 'Accetto la ',
    privacy_b: 'privacy policy',
    privacy_c: ' e il trattamento dei miei dati. *',
    newsletter: 'Acconsento a ricevere la newsletter e comunicazioni email da Prime Vision. *',
    whatsapp: 'Acconsento a ricevere comunicazioni WhatsApp da Prime Vision (promemoria, link e aggiornamenti). *',
  },
  en: {
    privacy_a: 'I accept the ',
    privacy_b: 'privacy policy',
    privacy_c: ' and the processing of my data. *',
    newsletter: 'I agree to receive the newsletter and email communications from Prime Vision. *',
    whatsapp: 'I agree to receive WhatsApp communications from Prime Vision (reminders, links and updates). *',
  },
};

export const CONSENT_FIELDS = ['privacy_consent', 'email_newsletter_consent', 'whatsapp_newsletter_consent'];

export const consentInitial = {
  privacy_consent: false,
  email_newsletter_consent: false,
  whatsapp_newsletter_consent: false,
};

/**
 * Three mandatory consent checkboxes shared by every Prime Vision lead form:
 * privacy (GDPR), email newsletter, WhatsApp communications.
 * Never pre-checked — submit must be blocked until all three are accepted.
 */
export const ConsentChecklist = ({ form, onChange, errors = {}, testIdPrefix = 'pv-consent', accent = 'teal' }) => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;
  const accentOn =
    accent === 'orange'
      ? 'bg-pv-orange border-pv-orange shadow-[0_4px_12px_-2px_rgba(255,107,53,0.5)]'
      : 'bg-pv-teal border-pv-teal shadow-[0_4px_12px_-2px_rgba(27,127,184,0.5)]';

  const rows = [
    {
      field: 'privacy_consent',
      testId: `${testIdPrefix}-privacy`,
      label: (
        <>
          {c.privacy_a}
          <a
            href="https://www.primevision.app/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pv-teal underline-offset-2 hover:underline font-semibold"
          >
            {c.privacy_b}
          </a>
          {c.privacy_c}
        </>
      ),
    },
    { field: 'email_newsletter_consent', testId: `${testIdPrefix}-newsletter-consent`, label: c.newsletter },
    { field: 'whatsapp_newsletter_consent', testId: `${testIdPrefix}-whatsapp-consent`, label: c.whatsapp },
  ];

  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.field}>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              data-testid={r.testId}
              checked={!!form[r.field]}
              onChange={onChange(r.field)}
              className="sr-only peer"
            />
            <span
              className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-md border-2 transition-all shrink-0 ${
                form[r.field] ? accentOn : 'bg-white border-slate-300 group-hover:border-slate-400'
              }`}
            >
              {form[r.field] && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
            </span>
            <span className="text-[13px] text-slate-600 leading-relaxed">{r.label}</span>
          </label>
          {errors[r.field] && (
            <p className="mt-1.5 ml-8 text-[12px] text-red-500 font-semibold">{errors[r.field]}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default ConsentChecklist;
